/*

  Rightcolumn.ts

  Right column of board view component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { ts } from "./i18n";
import { gameUrl, glyph, nbsp, scrollMovelistToBottom } from "./util";
import { VnodeChildren, ComponentFunc, m, Component } from "./mithril";
import { PlayerName } from "./playername";
import { TwoLetter } from "./twoletter";
import { Chat } from "./chat";
import { Bag } from "./bag";
import { MoveListItem } from "./movelistitem";
import { HeaderLogo } from "./components";

interface IAttributes {
  view: IView;
}

const Movelist: ComponentFunc<IAttributes> = (initialVnode) => {
  // The move list tab

  const view = initialVnode.attrs.view;
  const model = view.model;
  const game = model.game;
  const state = model.state;

  function movelist(): VnodeChildren {
    let mlist = game ? game.moves : []; // All moves made so far in the game
    let r: VnodeChildren = [];
    let leftTotal = 0;
    let rightTotal = 0;
    for (let i = 0; i < mlist.length; i++) {
      let move = mlist[i];
      let [player, [co, tiles, score]] = move;
      if (player === 0)
        leftTotal = Math.max(leftTotal + score, 0);
      else
        rightTotal = Math.max(rightTotal + score, 0);
      const n = m(MoveListItem,
        {
          view,
          move,
          info: {
            leftTotal: leftTotal, rightTotal: rightTotal,
            player: player, co: co, tiles: tiles, score: score
          }
        }
      );
      r.push(n);
    }
    return r;
  }

  const bag = game ? game.bag : "";
  const newbag = game ? game.newbag : true;
  return {
    view: () => m(".movelist-container",
      [
        m(".movelist",
          {
            onupdate: () => { setTimeout(scrollMovelistToBottom); }
          },
          movelist()
        ),
        // Show the bag here on mobile
        state?.uiFullscreen ? "" : m(Bag, { bag, newbag })
      ]
    )
  };
};

const Games: ComponentFunc<IAttributes> = (initialVnode) => {
  // The game list tab

  const view = initialVnode.attrs.view;
  const model = view.model;

  function games(): VnodeChildren {
    let r: VnodeChildren = [];
    if (model.loadingGameList || model.loadingAllLists)
      return r;
    let gameList = model.gameList;
    if (gameList === null) {
      // No games to show now, but we'll load them
      // and they will be automatically refreshed when ready
      model.loadGameList();
      return r;
    }
    const game = model.game;
    const gameId = game ? game.uuid : "";
    for (let item of gameList) {
      if (item.uuid == gameId)
        continue; // Don't show this game
      if (!item.my_turn && !item.zombie)
        continue; // Only show pending games
      let opp: VnodeChildren;
      if (item.oppid === null)
        // Mark robots with a cog icon
        opp = [glyph("cog"), nbsp(), item.opp];
      else
        opp = [item.opp];
      let winLose = item.sc0 < item.sc1 ? ".losing" : "";
      let title = "Staðan er " + item.sc0 + ":" + item.sc1;
      // Add the game-timed class if the game is a timed game.
      // These will not be displayed in the mobile UI.
      r.push(
        m(".games-item" + (item.timed ? ".game-timed" : ""),
          { key: item.uuid, title: title },
          m(m.route.Link,
            { href: gameUrl(item.url) },
            [
              m(".at-top-left", m(".tilecount", m(".oc", opp))),
              m(".at-top-left",
                m(".tilecount.trans",
                  m(".tc" + winLose, { style: { width: item.tile_count.toString() + "%" } }, opp)
                )
              )
            ]
          )
        )
      );
    }
    return r;
  }

  return {
    view: () => m(".games", { style: "z-index: 6" }, games())
  };
};

interface ITabAttributes {
  view: IView;
  tabid: string;
  title: string;
  icon: string;
  alert?: boolean;
  funcSel?: () => void;
}

const Tab: Component<ITabAttributes> = {
  // A clickable tab for the right-side area content
  view: (vnode) => {
    const { view, tabid, title, icon, alert, funcSel } = vnode.attrs;
    const sel = view.selectedTab || "movelist";
    const game = view.model.game;
    return m(".right-tab" + (sel === tabid ? ".selected" : ""),
      {
        id: "tab-" + tabid,
        className: alert ? "chat-alert" : "",
        title: title,
        onclick: (ev: Event) => {
          // Select this tab
          if (game && game.showingDialog === null) {
            if (view.setSelectedTab(tabid)) {
              // A new tab was actually selected
              funcSel && funcSel();
              if (tabid === "movelist")
                setTimeout(scrollMovelistToBottom);
            }
          }
          ev.preventDefault();
        }
      },
      glyph(icon)
    );
  }
};

const TabGroup: Component<{ view: IView }> = {
  view: (vnode) => {
    // A group of clickable tabs for the right-side area content
    const { view } = vnode.attrs;
    const { game } = view.model;
    const showChat = game && !game.isRobotGame();
    const r: VnodeChildren = [
      m(Tab, { view, tabid: "board", title: ts("Borðið"), icon: "grid" }),
      m(Tab, { view, tabid: "movelist", title: ts("Leikir"), icon: "show-lines" }),
      m(Tab, { view, tabid: "twoletter", title: ts("Tveggja stafa orð"), icon: "life-preserver" }),
      m(Tab, { view, tabid: "games", title: ts("Viðureignir"), icon: "flag" }),
    ];
    if (showChat) {
      // Add chat tab
      r.push(
        m(Tab, {
          view,
          tabid: "chat",
          title: ts("Spjall"),
          icon: "conversation",
          funcSel: () => {
            // The tab has been clicked
            if (game.markChatShown())
              // ...and now the user has seen all chat messages up until now
              m.redraw();
          },
          // Show chat icon in red if any chat messages have not been seen
          // and the chat tab is not already selected
          alert: !game.chatSeen && view.selectedTab !== "chat"
        })
      );
    }
    return m.fragment({}, r);
  }
};

export const RightColumn: ComponentFunc<IAttributes> = (initialVnode) => {
  // A container for the right-side header and area components

  const { view } = initialVnode.attrs;
  const { model } = view;
  const { game } = model;

  function vwClock(): VnodeChildren {
    // Show clock data if this is a timed game
    if (!game || !game.showClock())
      // Not a timed game, or a game that is completed
      return m.fragment({}, []);

    function vwClockFace(cls: string, txt: string, runningOut: boolean, blinking: boolean): VnodeChildren {
      return m("h3." + cls
        + (runningOut ? ".running-out" : "")
        + (blinking ? ".blink" : ""),
        txt
      );
    }

    return m.fragment({}, [
      vwClockFace("clockleft", game.clockText0, game.runningOut0, game.blinking0),
      vwClockFace("clockright", game.clockText1, game.runningOut1, game.blinking1),
      m(".clockface", glyph("time"))
    ]);
  }

  function vwRightHeading(): VnodeChildren {
    // The right-side heading on the game screen

    const fairplay = game ? game.fairplay : false;
    const player = game ? game.player : null;
    const sc0 = game ? game.displayScore(0).toString() : "";
    const sc1 = game ? game.displayScore(1).toString() : "";
    return m(".heading",
      [
        // The header-logo is not displayed in fullscreen
        m(".logowrapper", m(HeaderLogo)),
        m(".playerwrapper", [
          m(".leftplayer" + (player === 1 ? ".autoplayercolor" : ".humancolor"), [
            m(".player", m(PlayerName, { view, side: "left" })),
            m(".scorewrapper", m(".scoreleft", sc0)),
          ]),
          m(".rightplayer" + (player === 1 ? ".humancolor" : ".autoplayercolor"), [
            m(".player", m(PlayerName, { view, side: "right" })),
            m(".scorewrapper", m(".scoreright", sc1)),
          ]),
          m(".fairplay",
            { style: { visibility: fairplay ? "visible" : "hidden" } },
            m("span.fairplay-btn.large", { title: ts("Skraflað án hjálpartækja") })
          )
        ]),
        vwClock(),
      ]
    );
  }

  function vwRightArea(): VnodeChildren {
    // A container for the tabbed right-side area components
    if (!game) return undefined;
    const sel = view.selectedTab || "movelist";
    // Show the chat tab unless the opponent is an autoplayer
    let component: VnodeChildren;
    switch (sel) {
      case "movelist":
        component = m(Movelist, { view });
        break;
      case "twoletter":
        component = m(TwoLetter, { view });
        break;
      case "chat":
        component = m(Chat, { view });
        break;
      case "games":
        component = m(Games, { view });
        break;
      default:
        break;
    }
    const tabgrp: VnodeChildren = m(TabGroup, { view });
    return m(".right-area" + (game?.showClock() ? ".with-clock" : ""),
      component ? [tabgrp, component] : [tabgrp]
    );
  }

  function vwRightMessage(): VnodeChildren {
    // Display a status message in the mobile UI
    if (!game) return undefined;
    const s = game.buttonState();
    let msg: string | any[] = "";
    const player = game.player;
    const opp = player === null ? "" : game.nickname[1 - player];
    const move = game.moves.length ? game.moves[game.moves.length - 1] : undefined;
    const mtype = move ? move[1][1] : undefined;
    if (s.congratulate) {
      // This player won
      if (mtype == "RSGN")
        msg = [m("strong", [opp, " resigned!"]), " Congratulations."];
      else
        msg = [m("strong", ["You beat ", opp, "!"]), " Congratulations."];
    } else if (s.gameOver) {
      // This player lost
      msg = "Game over!";
    } else if (!s.localTurn) {
      // It's the opponent's turn
      msg = ["It's ", opp, "'s turn. Plan your next move!"];
    } else if (s.tilesPlaced) {
      if (game.currentScore === undefined) {
        if (move === undefined)
          msg = ["Your first move must cover the ", glyph("target"), " start square."];
        else
          msg = "Tiles must be consecutive.";
      } else if (game.wordGood === false) {
        msg = ["Move is not valid, but would score ", m("strong", game.currentScore.toString()), " points."];
      } else {
        msg = ["Valid move, score ", m("strong", game.currentScore.toString()), " points."];
      }
    } else if (move === undefined) {
      // Initial move
      msg = [m("strong", "You start!"), " Cover the ", glyph("star"), " asterisk with your move."];
    }
    else {
      const co = move[1][0];
      let tiles = mtype || "";
      const score = move[1][2];
      if (co === "") {
        // Not a regular tile move
        if (tiles === "PASS")
          msg = [opp, " passed."];
        else if (tiles.indexOf("EXCH") === 0) {
          const numtiles = tiles.slice(5).length;
          msg = [
            opp, " exchanged ",
            numtiles.toString(),
            (numtiles === 1 ? " tile" : " tiles"),
            "."
          ];
        } else if (tiles === "CHALL") {
          msg = [opp, " challenged your move."];
        } else if (tiles === "RESP") {
          if (score < 0)
            msg = [opp, " successfully challenged your move."];
          else
            msg = [opp, " unsuccessfully challenged your move and lost 10 points."];
        }
      }
      else {
        // Regular tile move
        tiles = tiles.split("?").join(""); /* TBD: Display wildcard characters differently? */
        msg = [opp, " played ", m("strong", tiles),
          " for ", m("strong", score.toString()), " points"];
      }
    }
    return m(".message", msg);
  }

  return {
    view: () => m(".rightcol", [
      vwRightHeading(),
      vwRightArea(),
      /* vwRightMessage(), */
    ])
  };
};
